import EmblaCarousel from 'embla-carousel';

/**
 * Testimonials carousel — Embla-backed Alpine component (CSP-safe).
 *
 * The DOM structure follows Embla's conventions:
 *   .embla
 *     .embla__viewport   (x-ref="viewport")
 *       .embla__container
 *         .embla__slide
 *
 * On hover-capable pointers, a circular arrow follows the cursor inside the
 * Embla column. Left half → scrollPrev, right half → scrollNext.
 *
 * Alpine exposes only method/property references (no inline expressions),
 * so the component stays compliant with @alpinejs/csp.
 */
export default function testimonialsCarousel() {
    return {
        embla: null,
        active: 0,
        count: 0,

        _cursorEnabled: false,
        _reducedMotion: false,
        _cursorEl: null,
        _zoneEl: null,
        _quickX: null,
        _quickY: null,
        _zoneRect: null,
        _cursorHalf: 0,
        _entered: false,
        _resizeObserver: null,
        _gsap: null,

        init() {
            const viewport = this.$refs.viewport;
            if (!viewport) {
                return;
            }

            this.count = viewport.querySelectorAll('.embla__slide').length;

            this.embla = EmblaCarousel(viewport, {
                loop: true,
                align: 'start',
                skipSnaps: false,
                duration: 25,
            });

            this.embla.on('select', this.onSelect.bind(this));
            this.embla.on('reInit', this.onSelect.bind(this));
            this.onSelect();

            this._cursorEnabled = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
            this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            if (this._cursorEnabled && this.count > 1) {
                this._initCursor();
            }
        },

        onSelect() {
            if (!this.embla) {
                return;
            }
            this.active = this.embla.selectedScrollSnap();
        },

        get counterLabel() {
            return `${this.active + 1}`;
        },

        scrollNext() {
            this.embla?.scrollNext();
        },

        scrollPrev() {
            this.embla?.scrollPrev();
        },

        scrollTo(index) {
            this.embla?.scrollTo(index);
        },

        async _initCursor() {
            this._cursorEl = this.$refs.cursor;
            this._zoneEl = this.$refs.cursorZone;
            if (!this._cursorEl || !this._zoneEl) {
                return;
            }

            try {
                const { gsap } = await import('gsap');
                this._gsap = gsap;
            } catch {
                // GSAP unavailable — cursor feature gracefully degrades.
                return;
            }

            const duration = this._reducedMotion ? 0 : 0.15;
            this._quickX = this._gsap.quickTo(this._cursorEl, 'x', { duration, ease: 'power3' });
            this._quickY = this._gsap.quickTo(this._cursorEl, 'y', { duration, ease: 'power3' });

            this._refreshRect();

            this._resizeObserver = new ResizeObserver(() => this._refreshRect());
            this._resizeObserver.observe(this._zoneEl);
        },

        _refreshRect() {
            if (!this._zoneEl || !this._cursorEl) {
                return;
            }
            this._zoneRect = this._zoneEl.getBoundingClientRect();
            this._cursorHalf = this._cursorEl.offsetWidth / 2;
        },

        _pointerOffset(event) {
            if (!this._zoneRect) {
                return null;
            }
            const rawX = event.clientX - this._zoneRect.left;
            const rawY = event.clientY - this._zoneRect.top;
            return {
                x: rawX - this._cursorHalf,
                y: rawY - this._cursorHalf,
                rawX,
            };
        },

        _setDirection(rawX) {
            if (!this._cursorEl || !this._zoneRect) {
                return;
            }
            const direction = rawX < this._zoneRect.width / 2 ? 'left' : 'right';
            if (this._cursorEl.dataset.direction !== direction) {
                this._cursorEl.dataset.direction = direction;
            }
        },

        onCursorEnter(event) {
            if (!this._gsap || !this._cursorEl) {
                return;
            }
            this._refreshRect();
            const p = this._pointerOffset(event);
            if (!p) {
                return;
            }
            this._setDirection(p.rawX);
            this._gsap.set(this._cursorEl, { x: p.x, y: p.y, scale: 0, opacity: 0 });
            this._entered = true;

            if (this._reducedMotion) {
                this._gsap.set(this._cursorEl, { scale: 1 });
                this._gsap.to(this._cursorEl, { opacity: 1, duration: 0.15 });
            } else {
                this._gsap.to(this._cursorEl, {
                    scale: 1,
                    opacity: 1,
                    duration: 0.35,
                    ease: 'back.out(1.8)',
                });
            }
        },

        onCursorMove(event) {
            if (!this._entered || !this._quickX || !this._quickY) {
                return;
            }
            const p = this._pointerOffset(event);
            if (!p) {
                return;
            }
            this._quickX(p.x);
            this._quickY(p.y);
            this._setDirection(p.rawX);
        },

        onCursorLeave() {
            if (!this._gsap || !this._cursorEl) {
                return;
            }
            this._entered = false;
            this._cursorEl.dataset.pressed = 'false';

            if (this._reducedMotion) {
                this._gsap.to(this._cursorEl, { opacity: 0, duration: 0.15 });
            } else {
                this._gsap.to(this._cursorEl, {
                    scale: 0,
                    opacity: 0,
                    duration: 0.3,
                    ease: 'back.in(1.8)',
                });
            }
        },

        onCursorDown() {
            if (this._cursorEl) {
                this._cursorEl.dataset.pressed = 'true';
            }
        },

        onCursorUp() {
            if (this._cursorEl) {
                this._cursorEl.dataset.pressed = 'false';
            }
        },

        onCursorClick() {
            if (!this._cursorEl) {
                return;
            }
            if (this._cursorEl.dataset.direction === 'left') {
                this.scrollPrev();
            } else {
                this.scrollNext();
            }
        },

        destroy() {
            if (this._resizeObserver) {
                this._resizeObserver.disconnect();
                this._resizeObserver = null;
            }
            if (this._gsap && this._cursorEl) {
                this._gsap.killTweensOf(this._cursorEl);
            }
            this._cursorEl = null;
            this._zoneEl = null;
            this._quickX = null;
            this._quickY = null;
            this._gsap = null;
            this._zoneRect = null;
            this._entered = false;

            this.embla?.destroy();
            this.embla = null;
        },
    };
}
